import { useCallback, useRef } from "react";
import { executeExpression } from "../engine/expressionEngine.js";

export default function useFormLifecycle({ docRef, templateRef, settingsRef }) {

  const runFunctionRef = useRef(null);

  const execFunction = useCallback((expr, e) => {
    return executeExpression(expr, docRef, settingsRef.current, e, {
      runFunction: (funcStr) => runFunctionRef.current?.(funcStr),
    });
  }, [docRef, settingsRef]);

  const executeFunctionByUid = useCallback((uid) => {
    try {
      const template = templateRef.current;
      if (template?.Run !== undefined) {
        const runItem = template.Run.find((item) => item._uid === uid);
        if (runItem !== undefined) {
          execFunction(runItem.onChangeValue);
        }
      }
    } catch (error) {
      console.error("Error in executeFunctionByUid:", error);
    }
  }, [templateRef, execFunction]);

  const ExecuteMultipleFunctions = useCallback((object) => {
    if (!Array.isArray(object)) return;
    object.forEach((item) => {
      if (item.onChangeValue !== undefined) {
        execFunction(item.onChangeValue);
      }
    });
  }, [execFunction]);

  const runFunction = useCallback((funcStr) => {
    if (!funcStr) return;

    const functions = funcStr.split(",");
    if (functions.length === 0) return;

    for (const func of functions) {
      const template = templateRef.current;
      const funToRun = template?.Func?.find((item) => item._uid === func);
      if (funToRun !== undefined) {
        return execFunction(funToRun.onChangeValue);
      }
    }
    return undefined;
  }, [templateRef, execFunction]);

  runFunctionRef.current = runFunction;

  const validateAndNotify = useCallback(() => {
    const template = templateRef.current;
    let isValidationTriggered = false;
    const validations = template?.Validation || [];

    const messages = validations
      .filter(({ Value: condition, validationMessage: message }) =>
        condition && message && execFunction(condition)
      )
      .map(({ validationMessage: message }) => message);

    if (messages.length > 0) {
      isValidationTriggered = true;
      const notify = require("devextreme/ui/notify").default;
      messages.forEach((message, index) => {
        setTimeout(() => {
          notify(message, "error", 3000);
        }, index * 3000);
      });
    }

    return isValidationTriggered;
  }, [templateRef, execFunction]);

  const onFieldDataChanged = useCallback((e) => {
    if (e.dataField.includes("copyDetails")) {
      e.component.repaint();
    }
  }, []);

  return {
    execFunction,
    executeFunctionByUid,
    ExecuteMultipleFunctions,
    runFunction,
    validateAndNotify,
    onFieldDataChanged,
  };
}
